import { Link } from "react-router-dom";
import { ArrowRight, Heart, Globe2, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";

const values = [
  { icon: Heart, title: "No gatekeeping, ever", text: "Every visa fee, rent price, and hard lesson I learn goes straight onto this site. Information that helps a sister move safely should never be hoarded." },
  { icon: Globe2, title: "Thirty-five countries of field notes", text: "I've landed solo and with my child in 35 countries. I know what an airport feels like with a hungry, exhausted child, and which cities make a woman alone feel welcome." },
  { icon: Users, title: "The single-mother lens", text: "Most hijrah advice assumes a husband is handling all the preparations. This community is built for the women doing it themselves — and doing it beautifully." },
];


export default function About() {
  return (
    <div data-testid="about-page" className="pt-24 lg:pt-28 bg-cream grain relative">
      <Seo
        title="Meet Hijrah Mom — The Mother Behind the Field Guide"
        siteName="Hijrah Mom"
        description="Meet the New York-born mother behind Hijrah Mom: 20 years in medicine, 35 countries traveled, and a 2026 hijrah — building a no-gatekeeping community for single women and mothers."
        image="/og-image.jpg"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Eyebrow>Meet Hijrah Mom</Eyebrow>
          <h1 className="mt-4 font-serif font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Assalamu alaikum, sis. <span className="italic text-petal">I'm a New York mom making hijrah in 2026.</span>
          </h1>
          <p className="mt-6 text-sage leading-relaxed max-w-2xl" data-testid="about-intro">
            I'm an American mother of African descent — born, raised, and educated in New York City, with twenty years in the medical field and one child who is my whole heart. After a lifetime in the United States, I have decided, by the grace of Allah, to make hijrah in 2026.
          </p>
          <p className="mt-4 text-sage leading-relaxed max-w-2xl">
            Beginning at age 19, I've traveled to 35 countries and 27 states, taken a year-long backpacking career break, and lived in 6 different states. I've always done extensive research before I travel and never had a problem uncovering the information I needed. However, when I began researching hijrah for my own family, I discovered a niche that is rarely spoken about: the perspective of single women and single mothers making this move. So I'm building the community I couldn't find — honest, practical, and with no gatekeeping.
          </p>
          <p className="mt-4 font-serif italic text-xl text-petal">Join me in the journey.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="about-contact-btn" className="inline-flex items-center gap-2 bg-petal text-white text-sm font-semibold px-6 py-3 rounded-full shadow-md shadow-petal/25 hover:bg-pine transition-colors">
              Say salaam <ArrowRight size={15} />
            </Link>
            <Link to="/countries" data-testid="about-countries-btn" className="inline-flex items-center gap-2 border border-pine/25 text-pine text-sm font-semibold px-6 py-3 rounded-full hover:bg-blush-deep transition-colors">
              Read the country profiles
            </Link>
          </div>
        </div>
        <FadeUp className="lg:col-span-5 flex justify-center" delay={0.15}>
          <div className="relative">
            <div className="absolute -inset-6 rounded-full bg-blush-deep blur-2xl" />
            <img src="/logo.png" alt="Hijrah Mom logo" data-testid="about-logo" className="relative w-64 h-64 lg:w-80 lg:h-80 rounded-full shadow-2xl" />
          </div>
        </FadeUp>
      </section>

      <section className="bg-pine-ink text-cream grain relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Eyebrow dark>What I promise you</Eyebrow>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08} className="bg-pine-card/10 border border-cream/10 rounded-2xl p-6 lg:p-8">
                <v.icon size={22} className="text-petal-soft" />
                <h3 className="mt-4 font-serif font-semibold text-xl">{v.title}</h3>
                <p className="mt-2 text-sm text-sage-light leading-relaxed">{v.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
